import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Tables } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

type Clinic = Tables<'clinics'>;

interface ClinicContextType {
  currentClinic: Clinic | null;
  clinics: Clinic[];
  setCurrentClinic: (clinic: Clinic) => void;
  isLoading: boolean;
  selectedClinic: string | null; // Add selectedClinic for compatibility
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (context === undefined) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
};

interface ClinicProviderProps {
  children: ReactNode;
}

export const ClinicProvider: React.FC<ClinicProviderProps> = ({ children }) => {
  const [currentClinic, setCurrentClinicState] = useState<Clinic | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, canAccessClinic } = useAuth();

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const { data, error } = await supabase
          .from('clinics')
          .select('*')
          .eq('status', 'active')
          .order('name');

        if (error) throw error;

        if (data && data.length > 0) {
          // Filter clinics based on user permissions
          const accessibleClinics = data.filter(clinic => {
            // Map clinic codes to simplified IDs used in auth
            const clinicAuthId = clinic.code?.toLowerCase() === 'forum' ? 'forum' : 
                               clinic.code?.toLowerCase() === 'ngeeann' ? 'ngeeann' : 
                               clinic.id;
            return canAccessClinic(clinicAuthId);
          });

          setClinics(accessibleClinics);
          
          // Check localStorage for saved clinic preference
          let savedClinicId: string | null = null;
          try {
            savedClinicId = localStorage.getItem('selectedClinicId');
          } catch (storageError) {
            console.warn('Failed to read selectedClinicId from localStorage:', storageError);
          }
          const savedClinic = accessibleClinics.find(clinic => clinic.id === savedClinicId);
          
          if (savedClinic) {
            setCurrentClinicState(savedClinic);
          } else if (accessibleClinics.length > 0) {
            // Default to first accessible clinic
            setCurrentClinicState(accessibleClinics[0]);
            try {
              localStorage.setItem('selectedClinicId', accessibleClinics[0].id);
            } catch (storageError) {
              console.warn('Failed to persist selectedClinicId to localStorage:', storageError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching clinics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchClinics();
    }
  }, [user, canAccessClinic]);

  const setCurrentClinic = (clinic: Clinic) => {
    setCurrentClinicState(clinic);
    try {
      localStorage.setItem('selectedClinicId', clinic.id);
    } catch (storageError) {
      console.warn('Failed to persist selectedClinicId to localStorage:', storageError);
    }
  };

  const selectedClinic = currentClinic?.id || null;

  return (
    <ClinicContext.Provider value={{ currentClinic, clinics, setCurrentClinic, isLoading, selectedClinic }}>
      {children}
    </ClinicContext.Provider>
  );
};